import React, { useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '@ohif/ui';

// Route Components
import DataSourceWrapper from './DataSourceWrapper';
import WorkList from './WorkList';
// import Local from './Local';
// import Debug from './Debug';
import NotFound from './NotFound';
import buildModeRoutes from './buildModeRoutes';
import PrivateRoute from './PrivateRoute';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

const StudyTokenForm = () => {
  const [studyId, setStudyId] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const onSubmit = e => {
    e.preventDefault();
    const sid = studyId.trim();
    const t = token.trim();
    if (!sid || !t) {
      setError('Debe completar ambos campos');
      return;
    }
    setError('');
    window.location.href = `/viewer?StudyInstanceUIDs=${encodeURIComponent(sid)}&token=${encodeURIComponent(t)}`;
  };

  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-col w-full max-w-xl gap-4 p-6 shadow-lg rounded-2xl bg-black/30"
    >
      <p className="text-xl font-bold text-center text-white">
        Ingresá el ID de estudio y el token para abrir el visor.
      </p>
      <div className="flex flex-col">
        <label
          htmlFor="studyId"
          className="mb-1 text-lg text-white/90"
        >
          Estudio
        </label>
        <input
          id="studyId"
          type="text"
          value={studyId}
          onChange={e => setStudyId(e.target.value)}
          className="p-2 text-white border rounded-md outline-none border-white/20 bg-black/40 focus:border-white/50"
        />
      </div>
      <div className="flex flex-col">
        <label
          htmlFor="token"
          className="mb-1 text-lg text-white/90"
        >
          Token
        </label>
        <input
          id="token"
          type="text"
          value={token}
          onChange={e => setToken(e.target.value)}
          className="p-2 text-white border rounded-md outline-none border-white/20 bg-black/40 focus:border-white/50"
        />
      </div>
      {error ? <div className="text-sm text-red-300">{error}</div> : null}
      <button
        type="submit"
        className="px-4 py-2 font-semibold text-black rounded-md bg-white/90 hover:bg-white"
      >
        Abrir estudio
      </button>
    </form>
  );
};

const NotFoundServer = ({
  message = 'Unable to query for studies at this time. Check your data source configuration or network connection',
}) => {
  return (
    <div className="absolute flex items-center justify-center w-full h-full text-white">
      <div>
        <h4>No ha sido posible obtener la información deseada.</h4>
      </div>
    </div>
  );
};

NotFoundServer.propTypes = {
  message: PropTypes.string,
};

const NotFoundStudy = () => {
  return (
    <div className="absolute flex items-center justify-center w-full h-full text-white bg-gradient-to-br from-green-300 via-emerald-200 to-green-400">
      <div className="p-8 rounded-md bg-black/50">
        {/* <h4>
          One or more of the requested studies are not available at this time. Return to the{' '}
          <Link
            className="text-primary-light"
            to={'/'}
          >
            study list
          </Link>{' '}
          to select a different study to view.
        </h4> */}
        <h4 className="text-2xl font-bold">
          El estudio solicitado no está disponible o el enlace ha caducado.
        </h4>
        <div className="flex justify-center mt-6">
          <Link
            to="/"
            className="px-4 py-2 font-semibold text-black rounded-md bg-white/90 hover:bg-white"
          >
            ¿Tiene un token?
          </Link>
        </div>
      </div>
    </div>
  );
};

NotFoundStudy.propTypes = {
  message: PropTypes.string,
};

// TODO: Include "routes" debug route if dev build
const bakedInRoutes = [
  {
    path: '/notfoundserver',
    children: NotFoundServer,
  },
  {
    path: '/notfoundstudy',
    children: NotFoundStudy,
  },
  // {
  //   path: '/debug',
  //   children: Debug,
  // },
  // {
  //   path: '/local',
  //   children: Local.bind(null, { modePath: '' }), // navigate to the worklist
  // },
  // {
  //   path: '/localbasic',
  //   children: Local.bind(null, { modePath: 'viewer/dicomlocal' }),
  // },
];

// NOT FOUND (404)
const notFoundRoute = { component: NotFound };

const createRoutes = ({
  modes,
  dataSources,
  extensionManager,
  servicesManager,
  commandsManager,
  hotkeysManager,
  routerBasename,
  showStudyList,
}: withAppTypes) => {
  const routes =
    buildModeRoutes({
      modes,
      dataSources,
      extensionManager,
      servicesManager,
      commandsManager,
      hotkeysManager,
    }) || [];

  const { customizationService } = servicesManager.services;

  const WorkListRoute = {
    path: '/',
    children: DataSourceWrapper,
    private: true,
    props: { children: WorkList, servicesManager, extensionManager },
  };

  const BaseRouteViewComponent = () => {
    return (
      <div className="flex flex-col items-center justify-center w-full h-screen gap-2 text-black bg-gradient-to-br from-green-300 via-emerald-200 to-green-400">
        <h1 className="mb-2 text-3xl font-bold text-white drop-shadow-lg">EasyDICOM</h1>
        <StudyTokenForm />
      </div>
    );
  };

  const BaseRouteView = {
    path: '/',
    children: BaseRouteViewComponent,
    private: true,
    props: { servicesManager, extensionManager },
  };

  const customRoutes = customizationService.getGlobalCustomization('customRoutes');
  const allRoutes = [
    ...routes,
    ...(showStudyList ? [WorkListRoute] : [BaseRouteView]),
    ...(customRoutes?.routes || []),
    ...bakedInRoutes,
    customRoutes?.notFoundRoute || notFoundRoute,
  ];

  function RouteWithErrorBoundary({ route, ...rest }) {
    // eslint-disable-next-line react/jsx-props-no-spreading
    return (
      <ErrorBoundary
        context={`Route ${route.path}`}
        fallbackRoute="/"
      >
        <route.children
          {...rest}
          {...route.props}
          route={route}
          servicesManager={servicesManager}
          extensionManager={extensionManager}
          hotkeysManager={hotkeysManager}
        />
      </ErrorBoundary>
    );
  }

  const { userAuthenticationService } = servicesManager.services;

  // All routes are private by default and then we let the user auth service
  // to check if it is enabled or not
  // Todo: I think we can remove the second public return below
  return (
    <Routes>
      {allRoutes.map((route, i) => {
        return route.private === true ? (
          <Route
            key={i}
            path={route.path}
            element={
              <PrivateRoute
                handleUnauthenticated={() => userAuthenticationService.handleUnauthenticated()}
              >
                <RouteWithErrorBoundary route={route} />
              </PrivateRoute>
            }
          ></Route>
        ) : (
          <Route
            key={i}
            path={route.path}
            element={<RouteWithErrorBoundary route={route} />}
          />
        );
      })}
    </Routes>
  );
};

export default createRoutes;
